import { COMPLETE_ASSET_UPLOAD, GET_ASSET, GET_ASSET_ARCHIVE, GET_ASSET_BY_REVISION_ID, GET_ASSET_FOR_DOWNLOAD, GET_ASSET_REVISION_FOR_DOWNLOAD, GET_ASSETS_LIST, MOVE_ASSET, REMOVE_ASSET, REMOVE_ASSET_REVISION, RENAME_ASSET, RENAME_ASSET_REVISION, REQUEST_ASSET_UPLOAD_URL, UPLOAD_ASSET } from "./gql";
import { Asset, AssetPermissions, AssetRevision, CreateAssetMutationInput, UpdateAssetMutationInput } from "../../../cms-glue/gql";
import { ApolloGQLClient, AssetMap, MutationBlob } from "../ApolloGQLClient";

export default class GQLAssets 
{
	static log = window._env_.ENVIRONMENT === "development" ? console : { info: () => {} };

	static generateAssetMap(assets: Asset[], permissions?: AssetPermissions) 
	{
		const assetMap: AssetMap = {};

		for (const asset of assets) 
		{
			if (asset && asset.databaseId) 
			{
				assetMap[asset.databaseId] = {
					id: asset.databaseId,
					name: asset.name,
					type: asset.type?.toLowerCase(),
					permissions: permissions ?? asset.permissions
				}

				if (asset.folder?.databaseId) 
				{
					assetMap[asset.databaseId].folder = asset.folder?.databaseId;
				}

				if (asset.originalFile) 
				{
					assetMap[asset.databaseId]['raw_hash'] = "";
					assetMap[asset.databaseId]['raw_size'] = asset.originalFile?.fileSize;
				}

				if (asset.processedFile) 
				{
					assetMap[asset.databaseId]['processed_hash'] = "";
					assetMap[asset.databaseId]['processed_size'] = asset.processedFile?.fileSize;
				}

				if (asset.previewFile) 
				{
					assetMap[asset.databaseId]['preview_hash'] = "";
					assetMap[asset.databaseId]['preview_size'] = asset.previewFile?.fileSize;
				}

				if (asset.errorProcessing) 
				{
					assetMap[asset.databaseId]['process_error'] = true;
				}

				if (asset.createdAt) 
				{
					assetMap[asset.databaseId]['creation_date'] = asset.createdAt;
				}

				if (asset.originalFileExtension) 
				{
					let ext = asset.originalFileExtension;

					if (ext.charAt(0) === '.') 
					{
						ext = ext.substring(1);
					}

					assetMap[asset.databaseId]['extension'] = ext;
				}

				if (asset.revisions && asset.revisions.length > 0) 
				{ // this asset has revisions
					assetMap[asset.databaseId]['revisions'] = [];

					for (const revision of asset.revisions) 
					{
						const revisionToPush = {
							id: revision.databaseId,
							name: revision.name,
							creation_date: revision.createdAt,
							processed_file_size: revision.processedFile?.fileSize,
							processed_file_hash: "",
							preview_file_size: revision.previewFile?.fileSize,
							preview_file_hash: "",
							metadata_file_size: revision.metadataFile?.fileSize,
							metadata_file_hash: ""
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
						} as any;

						if ('meshCount' in revision) 
						{
							revisionToPush['mesh_count'] = revision.meshCount;
						}

						if ('polyCount' in revision) 
						{
							revisionToPush['poly_count'] = revision.polyCount;
						}

						if ('hasAnimations' in revision) 
						{
							revisionToPush['has_animations'] = revision.hasAnimations;
						}

						if ('hasSkeleton' in revision) 
						{
							revisionToPush['has_skeleton'] = revision.hasSkeleton;
						}

						assetMap[asset.databaseId]['revisions'].push(revisionToPush);
					}

					if ('meshCount' in asset) 
					{
						assetMap[asset.databaseId]['mesh_count'] = asset.meshCount;
					}

					if ('polyCount' in asset) 
					{
						assetMap[asset.databaseId]['poly_count'] = asset.polyCount;
					}

					if ('hasAnimations' in asset) 
					{
						assetMap[asset.databaseId]['has_animations'] = asset.hasAnimations;
					}

					if ('hasSkeleton' in asset) 
					{
						assetMap[asset.id]['has_skeleton'] = asset.hasSkeleton;
					}
				}
			}
		}

		return assetMap;
	}

	static async uploadAsset(blob: Blob, fileName: string)
	{
		const variables = {
			input: {
				file: {
					blob,
					fileName
				} as MutationBlob
			} as CreateAssetMutationInput
		};

		// In order to upload a file, a multipart request is created.
		const data = await ApolloGQLClient.mutate<CreateAssetMutationInput>(UPLOAD_ASSET, variables, 'request to upload an asset');

		const asset: Asset = data.createAsset.asset;
		const errors = data.createAsset.errors;

		this.log.info({
			assetId: asset?.databaseId,
			data
		}, 'response to upload an asset');

		return { asset, errors };
	}

	static async requestAssetUploadUrl(fileName: string)
	{
		const variables = {
			input: {
				fileName,
				requestFileUploadUrl: true
			} as CreateAssetMutationInput
		};

		const data = await ApolloGQLClient.mutate<CreateAssetMutationInput>(REQUEST_ASSET_UPLOAD_URL, variables, 'request to request an asset upload url');

		console.info({
			data
		}, 'response to request an asset upload url');

		const asset: Asset = data.createAsset.asset;
		const errors = data.createAsset.errors;

		return { asset, errors };
	}

	static async completeAssetUpload(databaseId: string)
	{
		const variables = {
			input: {
				databaseId,
				completeFileUpload: true
			}
		};

		const data = await ApolloGQLClient.mutate<{ databaseId: string, completeFileUpload: boolean}>(COMPLETE_ASSET_UPLOAD, variables, 'request to complete an asset upload');

		console.info({
			data
		}, 'response to complete an asset upload');

		const asset: Asset = data.updateAsset.asset;
		const errors = data.updateAsset.errors;

		return { asset, errors };
	}

	static async renameAsset(databaseId: string, name: string)
	{
		const variables = {
			input: {
				databaseId,
				name
			}
		};

		const data = await ApolloGQLClient.mutate<{ databaseId: string, name: string}>(RENAME_ASSET, variables, 'request to rename an asset');

		const asset: Asset = data.updateAsset.asset;
		const errors = data.updateAsset.errors;

		console.info({
			data
		}, 'response to rename an asset');

		return { asset, errors };
	}

	static async renameAssetRevision(databaseId: string, name: string)
	{
		const variables = {
			input: {
				databaseId,
				name
			}
		};

		const data = await ApolloGQLClient.mutate<{ databaseId: string, name: string}>(RENAME_ASSET_REVISION, variables, 'request to rename an asset revision');

		const asset_revision: AssetRevision = data.updateAssetRevision.assetRevision;
		const errors = data.updateAssetRevision.errors;

		console.info({
			data
		}, 'response to rename an asset revision');

		return { asset_revision, errors };
	}

	static async moveAsset(databaseId: string, folderDatabaseId: string, ownerId: string)
	{

		const variables = {
			input: {
				databaseId,
				folderDatabaseId,
				ownerId
			} as UpdateAssetMutationInput
		};

		const data = await ApolloGQLClient.mutate<UpdateAssetMutationInput >(MOVE_ASSET, variables, 'request to move asset');

		const asset: Asset = data.updateAsset.asset;
		const errors = data.updateAsset.errors;

		console.info({
			data
		}, 'response to move asset');

		return { asset, errors };
	}

	static async removeAsset(databaseId: string)
	{
		const variables = {
			input: {
				databaseId,
				ownerId: null
			}
		};

		const data = await ApolloGQLClient.mutate<{ databaseId: string, ownerId: null}>(REMOVE_ASSET, variables, 'request to remove an asset');

		const asset: Asset = data.updateAsset.asset;
		const errors = data.updateAsset.errors;

		console.info({
			data
		}, 'response to remove an asset');

		return { asset, errors };
	}

	static async removeAssetRevision(databaseId: string, revisionDatabaseId: string)
	{
		const variables = {
			input: {
				databaseId,
				removeRevisionsDatabaseIds: [revisionDatabaseId]
			}
		};

		const data = await ApolloGQLClient.mutate<{ databaseId: string, removeRevisionsDatabaseIds: string[]}>(REMOVE_ASSET_REVISION, variables, 'request to remove an asset revision');

		const asset: Asset = data.updateAsset.asset;
		const errors = data.updateAsset.errors;

		console.info({
			data
		}, 'response to remove an asset revision');

		return { asset, errors };
	}

	static async getAssetArchiveInfo(databaseId: string)
	{
		const variables = {
			databaseId
		};

		const data = await ApolloGQLClient.query(GET_ASSET_ARCHIVE, variables, 'request to get an asset with archive info');

		const asset: Asset = data.asset;

		console.info({
			data
		}, 'response to getting an asset with archive info');

		return asset;
	}

	static async getAssetForDownload(databaseId: string, loadType: string)
	{
		const variables = {
			databaseId,
			loadPreview: loadType === "preview",
			loadProcessed: loadType === "processed"
		};

		const data = await ApolloGQLClient.query(GET_ASSET_FOR_DOWNLOAD, variables, 'request to get an asset for download');

		const asset: Asset = data.asset;

		console.info({
			data
		}, 'response to getting an asset for download');

		return asset;
	}

	static async getAssetRevisionForDownload(databaseId: string, loadType: string)
	{
		const variables = {
			databaseId,
			loadMetadata: loadType === "metadata",
			loadPreview: loadType === "preview",
			loadProcessed: loadType === "processed"
		};

		const data = await ApolloGQLClient.query(GET_ASSET_REVISION_FOR_DOWNLOAD, variables, 'request to get an asset revision for download');

		const assetRevision: AssetRevision = data.assetRevision;

		console.info({
			data
		}, 'response to getting an asset revision for download');

		return assetRevision;
	}

	static async getAsset(databaseId: string)
	{
		const variables = {
			databaseId
		};

		const data = await ApolloGQLClient.query(GET_ASSET, variables, 'request to get an asset info');

		const asset: Asset = data.asset;

		console.info({
			data
		}, 'response to getting an asset info');

		return asset;
	}

	static async getAssetByRevisionId(databaseId: string)
	{
		const variables = {
			databaseId
		};

		const data = await ApolloGQLClient.query(GET_ASSET_BY_REVISION_ID, variables, 'request to get an asset info by revision ID');

		const revision: AssetRevision = data.assetRevision;

		console.info({
			data
		}, 'response to getting an asset info by revision ID');

		return revision?.asset;
	}

	static async getAssetsList()
	{
		const variables = {};

		const data = await ApolloGQLClient.query(GET_ASSETS_LIST, variables, 'request to get the assets list');
		
		console.info({
			data
		}, 'response to getting the assets list');

		return {
			user: data.viewer.assets?.nodes,
			organization: data.viewer.organization.assets?.nodes
		};
	}
}